import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import axios from "axios";
import WatchPartyCard from "../components/WatchPartyCard";

const PageShell = ({ children }) => (
  <div className="relative min-h-screen flex flex-col items-center justify-start px-3 sm:px-4 md:px-6 py-6 sm:py-10 overflow-hidden bg-transparent">
    <style>{`
      @keyframes subtleFloat {
        0%, 100% { transform: translate(-50%, -50%) scale(1); }
        50% { transform: translate(-50%, -52%) scale(1.03); }
      }
      @keyframes ringWave {
        0% { transform: translate(-50%, -50%) scale(0.35); opacity: 0.85; }
        60% { transform: translate(-50%, -50%) scale(0.75); opacity: 0.45; }
        100% { transform: translate(-50%, -50%) scale(0.95); opacity: 0; }
      }
    `}</style>

    {/* background (transparent now) */}
    <div className="absolute inset-0 bg-transparent pointer-events-none" />

    {/* grid overlay */}
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.2]"
      style={{
        backgroundImage:
          "linear-gradient(to right, rgba(15,23,42,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.4) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }}
    />

    {/* center orb */}
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 w-[15vw] max-w-[165px] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{
        background:
          "radial-gradient(circle at 50% 40%, rgba(56,189,248,0.28), transparent 60%), radial-gradient(circle at 50% 75%, rgba(15,23,42,0.95), transparent 85%)",
        boxShadow: "0 0 80px rgba(56,189,248,0.32)",
        animation: "subtleFloat 20s ease-in-out infinite",
      }}
    />

    {/* RING 1 – fires instantly */}
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
      style={{
        width: "58vw",
        height: "58vw",
        border: "2px solid rgba(45,212,191,0.6)",
        transform: "translate(-50%, -50%) scale(0.35)",
        opacity: 0,
        animation: "ringWave 12s linear infinite",
        animationFillMode: "forwards",
      }}
    />

    {/* RING 2 – delayed 4 seconds */}
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
      style={{
        width: "58vw",
        height: "58vw",
        border: "2px solid rgba(45,212,191,0.45)",
        transform: "translate(-50%, -50%) scale(0.35)",
        opacity: 0,
        animation: "ringWave 12s linear infinite",
        animationDelay: "4s",
        animationFillMode: "forwards",
      }}
    />

    {/* RING 3 – delayed 8 seconds */}
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
      style={{
        width: "58vw",
        height: "58vw",
        border: "2px solid rgba(45,212,191,0.3)",
        transform: "translate(-50%, -50%) scale(0.35)",
        opacity: 0,
        animation: "ringWave 12s linear infinite",
        animationDelay: "8s",
        animationFillMode: "forwards",
      }}
    />

    {/* content */}
    <div className="relative z-10 w-full max-w-6xl">{children}</div>
  </div>
);

function WatchPartiesPage() {
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    let isMounted = true;

    const fetchParties = async () => {
      try {
        const response = await axios.get("/api/watch-parties");
        if (isMounted) setParties(response.data);
      } catch (error) {
        console.error("Error fetching watch parties:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchParties();
    const interval = setInterval(fetchParties, 10000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const filteredParties = parties.filter((party) => {
    if (party.status === "ended") return false;
    if (filter === "Live now") return party.status === "live";
    if (filter === "Starting soon") return party.status === "waiting";
    return true;
  });

  const totalRooms = parties.length;
  const liveRooms = parties.filter((p) => p.status === "live").length;

  return (
    // Mobile: let the page scroll naturally (no nested scroll traps)
    // Desktop+: preserve your existing "panel scroll" feel
    <div className="min-h-screen p-4 sm:p-5 md:p-6 sm:h-full sm:overflow-y-auto sm:scrollbar-hide">
      <PageShell>
        {/* Header */}
        <header className="mb-4 sm:mb-6 flex flex-col items-center px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-50 tracking-[0.16em] uppercase text-center">
            Watch Parties
          </h1>
          <p className="mt-2 text-xs sm:text-[11px] md:text-xs text-slate-300/85 text-center max-w-md">
            Join live watch parties or create your own to watch anime with friends
          </p>
          <div className="mt-3 h-px w-20 sm:w-28 bg-gradient-to-r from-transparent via-emerald-400/80 to-transparent" />

          {/* Create Party Button */}
          <button
            onClick={() => navigate("/start-watch-party")}
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-400 text-white font-semibold rounded-xl hover:from-emerald-400 hover:to-emerald-300 transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:-translate-y-0.5 active:scale-95"
          >
            <Plus size={20} />
            <span>Create Watch Party</span>
          </button>
        </header>

        {/* Status + filters row */}
        <div className="mb-4 sm:mb-5 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-3 text-[10px] sm:text-[11px] text-slate-200">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/70 border border-slate-700/70 px-3 py-1.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            <span>
              {liveRooms} live • {totalRooms - liveRooms} idle
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap justify-center">
            {["All", "Live now", "Starting soon"].map((label) => (
              <button
                key={label}
                onClick={() => setFilter(label)}
                className={`px-2.5 sm:px-3 py-1.5 rounded-full border text-[10px] sm:text-[11px] transition ${
                  filter === label
                    ? "border-emerald-400/80 bg-emerald-400/10 text-emerald-100"
                    : "border-slate-600/70 bg-slate-900/70 text-slate-300 hover:border-emerald-400/60 hover:text-emerald-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid of parties */}
        <section className="space-y-3 sm:space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="text-slate-400 text-sm">Loading parties...</div>
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="flex justify-center py-12">
              <div className="text-slate-400 text-sm">
                No watch parties found. Be the first to create one!
              </div>
            </div>
          ) : (
            // Ensure small screens never force awkward card widths:
            // - 1 col by default
            // - 2 cols on xs and up (if you have xs configured)
            // - keep your desktop layout identical
            <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {filteredParties.map((party) => (
                <WatchPartyCard
                  key={party.id}
                  partyId={party.id}
                  animeTitle={party.title}
                  hostName={party.host_username}
                  avatarUrl={party.host_avatar}
                  platform={party.platform}
                  slotsUsed={party.participants_count}
                  slotsTotal={party.max_capacity}
                  isLive={party.status === "live"}
                  isPublic={party.is_public !== false}
                />
              ))}
            </div>
          )}
        </section>
      </PageShell>
    </div>
  );
}

export default WatchPartiesPage;
